import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupportReport } from '../api/supportApi';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ThreadView from '../components/support/ThreadView';
import MainLayout from '../components/layout/MainLayout';
import { ROUTES } from '../utils/constants';

const MyReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  // Fetch support report details
  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    }
  }, [reportId]);

  const fetchReportDetails = async () => {
    setLoading(true);
    try {
      console.log(`[MY-REPORT-DETAIL] Fetching report with ID: ${reportId}`);
      const data = await getSupportReport(reportId);
      console.log(`[MY-REPORT-DETAIL] Report data received:`, data);

      // Verify this report belongs to the current user
      if (data.userId !== currentUser?.uid) {
        console.error('[MY-REPORT-DETAIL] Report does not belong to current user');
        setError('You do not have permission to view this report');
        toast.error('You do not have permission to view this report');
        return;
      }

      setReport(data);
      setError(null);
    } catch (err) {
      console.error(`[MY-REPORT-DETAIL] Error fetching report ${reportId}:`, err);
      const errorMessage = err.message || 'Unknown error';
      setError(`Failed to load support report details: ${errorMessage}`);
      toast.error(`Error: ${errorMessage}`);

      // If this is a backend error, show a more specific message
      if (errorMessage.includes('db is not defined')) {
        toast.info('Backend database issue detected. Please try again later.', {
          position: 'top-center',
          autoClose: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';

    // If timestamp is a Firestore timestamp, convert it
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center my-8">
          <LoadingSpinner />
        </div>
      </MainLayout>
    );
  }

  if (error || !report) {
    return (
      <MainLayout>
        <div
          className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
          role="alert"
        >
          <p className="font-bold">Error Loading Report</p>
          <p>{error || 'Report not found'}</p>
        </div>

        <button
          onClick={() => navigate(`${ROUTES.SUPPORT_REPORTS}?tab=mine`)}
          className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Back to Support
        </button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Support Report Details</h1>
        <button
          onClick={() => navigate(`${ROUTES.SUPPORT_REPORTS}?tab=mine`)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Back to Support
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Details Card */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-xl font-semibold">{report.title}</h2>
              <div className="flex flex-wrap gap-3 mt-2">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                  ${
                    report.type === 'bug'
                      ? 'bg-red-100 text-red-800'
                      : report.type === 'feature'
                        ? 'bg-green-100 text-green-800'
                        : report.type === 'feedback'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {report.type}
                </span>
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                  ${
                    report.status === 'open'
                      ? 'bg-yellow-100 text-yellow-800'
                      : report.status === 'pending'
                        ? 'bg-blue-100 text-blue-800'
                        : report.status === 'closed'
                          ? 'bg-green-100 text-green-800'
                          : report.status === 'wishlist'
                            ? 'bg-purple-100 text-purple-800'
                            : report.status === 'big_issue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {report.status}
                </span>
              </div>
              <div className="text-sm text-gray-500 mt-2">
                Created: {formatDate(report.createdAt)}
              </div>
              {report.updatedAt && report.updatedAt !== report.createdAt && (
                <div className="text-sm text-gray-500">Updated: {formatDate(report.updatedAt)}</div>
              )}
            </div>

            <div className="px-6 py-4">
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Description
                </h3>
                <p className="text-gray-700 whitespace-pre-line">{report.description}</p>
              </div>

              {/* {report.route && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">System Information</h3>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {report.route && (
                        <div>
                          <span className="block text-gray-500">Route</span>
                          <span className="text-gray-700">{report.route}</span>
                        </div>
                      )}
                      {report.app && (
                        <div>
                          <span className="block text-gray-500">App</span>
                          <span className="text-gray-700">{report.app}</span>
                        </div>
                      )}
                      {report.browser && (
                        <div>
                          <span className="block text-gray-500">Browser</span>
                          <span className="text-gray-700">{report.browser}</span>
                        </div>
                      )}
                      {report.platform && (
                        <div>
                          <span className="block text-gray-500">Platform</span>
                          <span className="text-gray-700">{report.platform}</span>
                        </div>
                      )}
                      {report.screenSize && (
                        <div>
                          <span className="block text-gray-500">Screen Size</span>
                          <span className="text-gray-700">{report.screenSize}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </div>

        {/* Thread View Card */}
        <div className="lg:col-span-2">
          <ThreadView reportId={reportId} />
        </div>
      </div>
    </MainLayout>
  );
};

export default MyReportDetailPage;
